import { createContentfulObject, contentfulTemplateMap } from '#src/services/contentful'
import { useContentfulByVariationQuery } from './Contentful.generated'
import { ContentfulData, ContentfulResult } from './types'
import { QueryHookOptions } from '@apollo/client'
import { invariant } from '#src/services/errors'

interface ContentfulQueryVariables extends Omit<QueryHookOptions, 'variables'> {
  spaceId?: string
  template?: string | null
  variation?: string | null
}

export default function useContentfulQuery<T extends ContentfulData = any>({
  spaceId,
  template,
  variation,
  ...options
}: ContentfulQueryVariables): ContentfulResult<T> {
  if (!spaceId) {
    // "template" is optional if `options.skip` is true, but in that case the spaceId isn't used.
    spaceId = contentfulTemplateMap[template!]
  }

  if (!options.skip && (!template || !variation)) {
    return invariant('A template and variation must be provided to "useContentfulQuery')
  }

  const { data, loading, error } = useContentfulByVariationQuery({
    variables: options.skip
      ? undefined
      : {
          // "template" and "variation" must be provided if the query is not being skipped
          template: template!,
          variation: variation!,
          space: spaceId
        },
    ...options
  })

  const content = data?.findContentByTemplate
    ? (createContentfulObject(data.findContentByTemplate.entries) as T)
    : undefined

  return { content, loading, error }
}
